import { z } from "zod";
import { assertUnreachable } from "../helpers/utilities.js";
import {
  automationGroupReportInstanceReferenceSchema,
  automationInstanceRecipientRoutingSchema,
  automationManagerReportInstanceReferenceSchema,
  automationMappedEntryDestinationSchema,
  automationReportDestinationSchema,
  automationReportRoutingRuleSchema
} from "./automationReportRouting.js";
import { groupIdSchema } from "./group.js";
import { playbookIdSchema } from "./playbook.js";
import { teammateIdSchema } from "./teammate.js";
const playbooksScopeSchema = z.array(playbookIdSchema).min(1).refine((playbookIds) => new Set(playbookIds).size === playbookIds.length, {
  message: "Playbook ids must be unique"
});
const allOrSpecificTeammatesSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("all") }),
  z.object({
    type: z.literal("specific"),
    teammate_ids: z.array(teammateIdSchema),
    group_ids: z.array(groupIdSchema)
  })
]);
const managerReportInstanceSelectionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("all") }),
  z.object({
    type: z.literal("specific"),
    teammate_ids: z.array(teammateIdSchema)
  })
]);
const groupReportInstanceSelectionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("all") }),
  z.object({
    type: z.literal("specific"),
    group_ids: z.array(groupIdSchema)
  })
]);
const legacyAutomationReportInstancesSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("shared") }),
  z.object({
    type: z.literal("teammates"),
    selection: allOrSpecificTeammatesSchema
  }),
  z.object({
    type: z.literal("managers"),
    selection: managerReportInstanceSelectionSchema
  }),
  z.object({
    type: z.literal("groups"),
    selection: groupReportInstanceSelectionSchema
  })
]);
function buildScopeReferenceSchema(configuredShape) {
  return z.discriminatedUnion("source", [
    z.object({
      source: z.literal("configured"),
      ...configuredShape
    }),
    z.object({ source: z.literal("instance") })
  ]);
}
const teammateScopeReferenceSchema = buildScopeReferenceSchema({
  teammate_id: teammateIdSchema
});
const managerScopeReferenceSchema = buildScopeReferenceSchema({
  manager_id: teammateIdSchema
});
const groupScopeReferenceSchema = buildScopeReferenceSchema({
  group_id: groupIdSchema
});
const legacyAutomationReportScopeRuleSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("playbook_teammates"),
    playbook_ids: playbooksScopeSchema,
    teammate_selection: allOrSpecificTeammatesSchema.optional()
  }),
  z.object({
    type: z.literal("teammates"),
    teammate: teammateScopeReferenceSchema,
    playbook_ids: playbooksScopeSchema
  }),
  z.object({
    type: z.literal("manager_teammates"),
    manager: managerScopeReferenceSchema,
    playbook_ids: playbooksScopeSchema
  }),
  z.object({
    type: z.literal("group_teammates"),
    group: groupScopeReferenceSchema,
    playbook_ids: playbooksScopeSchema
  })
]);
const groupReportRoutingSchema = z.discriminatedUnion("type", [
  ...automationReportDestinationSchema.options,
  z.object({
    type: z.literal("mapped"),
    entries: z.array(
      z.object({
        instance: automationGroupReportInstanceReferenceSchema,
        destination: automationMappedEntryDestinationSchema
      })
    ).refine(
      (entries) => new Set(entries.map(({ instance }) => instance.group_id)).size === entries.length,
      { message: "Mapped routing may contain only one entry per group" }
    )
  })
]);
const managerReportRoutingSchema = z.discriminatedUnion("type", [
  ...automationReportDestinationSchema.options,
  automationInstanceRecipientRoutingSchema,
  z.object({
    type: z.literal("mapped"),
    entries: z.array(
      z.object({
        instance: automationManagerReportInstanceReferenceSchema,
        destination: automationMappedEntryDestinationSchema
      })
    ).refine(
      (entries) => new Set(entries.map(({ instance }) => instance.manager_id)).size === entries.length,
      { message: "Mapped routing may contain only one entry per manager" }
    )
  })
]);
const legacyAutomationReportSynthesisSchema = z.discriminatedUnion("group_by", [
  z.object({
    group_by: z.literal("manager"),
    routing: automationInstanceRecipientRoutingSchema,
    /**
     * Which people may receive a manager synthesis, resolved against who
     * actually manages a team at plan time. Absent means every manager of a
     * planned teammate — the shape written before managers were individually
     * selectable, which every existing definition keeps meaning.
     */
    selection: z.object({
      type: z.literal("specific"),
      teammate_ids: z.array(teammateIdSchema),
      /**
       * Groups whose manager members are selected too, resolved against
       * current membership at plan time like the `instances` groups.
       */
      group_ids: z.array(groupIdSchema).optional()
    }).optional()
  }),
  z.object({
    group_by: z.literal("group"),
    routing: groupReportRoutingSchema
  })
]);
const reportDefinitionBaseSchema = z.object({
  instances: legacyAutomationReportInstancesSchema,
  scope: legacyAutomationReportScopeRuleSchema,
  routing: automationReportRoutingRuleSchema,
  synthesis: legacyAutomationReportSynthesisSchema.optional()
});
const sharedReportDefinitionSchema = reportDefinitionBaseSchema.extend({
  instances: z.object({ type: z.literal("shared") }),
  scope: z.object({
    type: z.literal("playbook_teammates"),
    playbook_ids: playbooksScopeSchema,
    teammate_selection: allOrSpecificTeammatesSchema.optional()
  }),
  routing: automationReportDestinationSchema,
  // Syntheses only exist over a per-teammate fan-out.
  synthesis: z.undefined().optional()
}).refine(
  (definition) => {
    const destination = definition.routing;
    return destination.type !== "recipients" || destination.subscribers.teammate_ids.length === 0 && destination.subscribers.group_ids.length === 0;
  },
  {
    path: ["routing", "subscribers"],
    message: "Custom automation subscribers are not supported yet"
  }
);
const perPersonReportDefinitionSchema = reportDefinitionBaseSchema.extend({
  instances: z.object({
    type: z.literal("teammates"),
    // Every teammate, or hand-picked people and groups. A group's members are
    // partitioned by role at plan time: managers fall out of this fan-out and
    // into the manager synthesis, as if picked individually.
    selection: allOrSpecificTeammatesSchema
  }),
  scope: z.object({
    type: z.literal("teammates"),
    teammate: z.object({ source: z.literal("instance") }),
    playbook_ids: playbooksScopeSchema
  }),
  routing: automationInstanceRecipientRoutingSchema
});
const perManagerReportDefinitionSchema = reportDefinitionBaseSchema.extend({
  instances: z.object({
    type: z.literal("managers"),
    selection: managerReportInstanceSelectionSchema
  }),
  scope: z.object({
    type: z.literal("manager_teammates"),
    manager: z.object({ source: z.literal("instance") }),
    playbook_ids: playbooksScopeSchema
  }),
  routing: managerReportRoutingSchema,
  // Syntheses only exist over a per-teammate fan-out.
  synthesis: z.undefined().optional()
});
const perGroupReportDefinitionSchema = reportDefinitionBaseSchema.extend({
  instances: z.object({
    type: z.literal("groups"),
    selection: groupReportInstanceSelectionSchema
  }),
  scope: z.object({
    type: z.literal("group_teammates"),
    group: z.object({ source: z.literal("instance") }),
    playbook_ids: playbooksScopeSchema
  }),
  routing: groupReportRoutingSchema,
  // Syntheses only exist over a per-teammate fan-out.
  synthesis: z.undefined().optional()
});
const legacyAutomationReportDefinitionSchema = z.union([
  sharedReportDefinitionSchema,
  perPersonReportDefinitionSchema,
  perManagerReportDefinitionSchema,
  perGroupReportDefinitionSchema
]);
function checkIsSharedLegacyReportDefinition(definition) {
  return definition.instances.type === "shared";
}
function readLegacyReportDefinition(legacy) {
  const definitions = [
    {
      instances: readLegacyInstances(legacy),
      source: "calls",
      routing: legacy.routing
    }
  ];
  if (legacy.synthesis) {
    definitions.push(readLegacySynthesis(legacy.synthesis));
  }
  return { playbook_ids: legacy.scope.playbook_ids, definitions };
}
function readLegacyInstances(legacy) {
  if (checkIsSharedLegacyReportDefinition(legacy)) {
    return {
      type: "shared",
      selection: legacy.scope.teammate_selection ?? { type: "all" }
    };
  }
  const { instances } = legacy;
  switch (instances.type) {
    case "teammates":
      return { type: "teammate", selection: instances.selection };
    case "groups":
      return { type: "group", selection: instances.selection };
    case "managers":
      return {
        type: "manager",
        selection: instances.selection.type === "all" ? instances.selection : { ...instances.selection, group_ids: [] }
      };
    default:
      return assertUnreachable(instances);
  }
}
function readLegacySynthesis(synthesis) {
  switch (synthesis.group_by) {
    case "manager":
      return {
        instances: {
          type: "manager",
          selection: synthesis.selection ? {
            type: "specific",
            teammate_ids: synthesis.selection.teammate_ids,
            group_ids: synthesis.selection.group_ids ?? []
          } : { type: "all" }
        },
        source: "rollup",
        routing: synthesis.routing
      };
    case "group":
      return {
        instances: { type: "group", selection: { type: "all" } },
        source: "rollup",
        routing: synthesis.routing
      };
    default:
      return assertUnreachable(synthesis);
  }
}
export {
  legacyAutomationReportDefinitionSchema,
  readLegacyReportDefinition
};
//# sourceMappingURL=automationReportDefinitionLegacy.js.map
